/**
 * CalendarPage — unified month/agenda view of all dated wedding items
 * (events, payment installments, checklist tasks).
 *
 * Two view modes (toggle in header):
 *   • Month grid — 7×6, items rendered as colored chips per date cell.
 *   • Agenda list — items grouped by date, sorted ascending.
 *
 * Backend: GET /api/calendar/{wedding_id}.
 * Each item has type ∈ {event, payment, task} with consistent colour mapping.
 * Click a chip → popover with detail + "Go to source" link to the owning page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { NavArrowLeft, NavArrowRight, Calendar as CalendarIcon, List, OpenNewWindow } from 'iconoir-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  event:   { label: 'Events',   color: 'terracotta', dest: '/events',    bg: 'bg-terracotta/10', text: 'text-terracotta', border: 'border-terracotta/30' },
  payment: { label: 'Payments', color: 'ochre',      dest: '/budget',    bg: 'bg-ochre/10',      text: 'text-ochre',      border: 'border-ochre/30' },
  task:    { label: 'Tasks',    color: 'moss',       dest: '/checklist', bg: 'bg-moss/10',       text: 'text-moss',       border: 'border-moss/30' },
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function ymdLocal(d) {
  // Build YYYY-MM-DD in local time so we don't shift dates near midnight UTC.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildMonthCells(year, monthIdx) {
  // Returns 42 cells (6 weeks × 7 days) starting from the Sunday on/before
  // the 1st, ending on the Saturday on/after the last day of the month.
  const first = new Date(year, monthIdx, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // back up to Sunday
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function formatAgendaDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function ItemChip({ item, currency, onActivate }) {
  const meta = TYPE_META[item.type];
  const dim = (item.type === 'task' && item.completed) || (item.type === 'payment' && item.paid);
  const overdue = item.overdue;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative block w-full truncate text-left px-1.5 py-0.5 rounded-sm text-[10px] font-mono ${overdue ? 'bg-red-50 text-red-700 ring-1 ring-red-300' : `${meta.bg} ${meta.text}`} hover:opacity-80 transition-opacity ${dim ? 'line-through opacity-60' : ''}`}
          onClick={onActivate}
          data-testid={`calendar-chip-${item.id}`}
          title={overdue ? `OVERDUE: ${item.title}` : item.title}
        >
          {overdue && (
            <span
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-red-600 ring-1 ring-white"
              data-testid={`calendar-chip-overdue-dot-${item.id}`}
              aria-hidden="true"
            />
          )}
          {item.title}
        </button>
      </PopoverTrigger>
      <ItemPopoverContent item={item} currency={currency} />
    </Popover>
  );
}

function ItemPopoverContent({ item, currency }) {
  const navigate = useNavigate();
  const meta = TYPE_META[item.type];
  return (
    <PopoverContent className="w-72 p-4 bg-white" data-testid={`calendar-popover-${item.id}`}>
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${meta.bg} ${meta.text} ${meta.border} border text-xs uppercase tracking-wider`}>
            {meta.label.replace(/s$/, '')}
          </Badge>
          {item.overdue && (
            <Badge
              className="bg-red-50 text-red-700 border border-red-300 text-xs uppercase tracking-wider"
              data-testid={`calendar-popover-overdue-${item.id}`}
            >
              Overdue
            </Badge>
          )}
          <span className="text-xs text-muted font-mono">{formatAgendaDate(item.date)}</span>
        </div>
        <h4 className="font-serif text-base text-charcoal leading-snug">{item.title}</h4>
        {item.type === 'payment' && (
          <p className="text-sm text-charcoal font-mono">
            {(item.currency || currency || '')} {Number(item.amount || 0).toLocaleString()}
            {item.paid && <span className="ml-2 text-moss text-xs uppercase">Paid</span>}
          </p>
        )}
        {item.type === 'task' && (
          <div className="flex items-center gap-2 text-xs text-muted">
            {item.priority && <Badge className="bg-charcoal/5 text-charcoal border border-border text-[10px] uppercase">{item.priority}</Badge>}
            {item.category && <span>{item.category}</span>}
            {item.completed && <span className="text-moss uppercase">Done</span>}
          </div>
        )}
        <Button
          type="button"
          onClick={() => navigate(meta.dest)}
          className="btn-secondary w-full text-xs flex items-center justify-center gap-2 mt-2"
          data-testid={`calendar-popover-source-${item.id}`}
        >
          <OpenNewWindow className="w-3.5 h-3.5" /> Go to {meta.label}
        </Button>
      </div>
    </PopoverContent>
  );
}

function MonthView({ year, monthIdx, itemsByDate, currency }) {
  const cells = useMemo(() => buildMonthCells(year, monthIdx), [year, monthIdx]);
  const todayIso = ymdLocal(new Date());
  return (
    <div data-testid="calendar-month-view">
      <div className="grid grid-cols-7 text-xs font-mono uppercase tracking-wider text-muted border-b border-border pb-2 mb-1">
        {WEEKDAYS.map((w) => <div key={w} className="text-center">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px bg-border rounded-sm overflow-hidden">
        {cells.map((d, i) => {
          const iso = ymdLocal(d);
          const inMonth = d.getMonth() === monthIdx;
          const isToday = iso === todayIso;
          const items = itemsByDate.get(iso) || [];
          return (
            <div
              key={i}
              className={`bg-white min-h-[88px] p-1.5 ${inMonth ? '' : 'bg-page/60'}`}
              data-testid={`calendar-day-${iso}`}
            >
              <div className={`text-xs font-mono ${inMonth ? 'text-charcoal' : 'text-muted'} ${isToday ? 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-terracotta text-white' : ''}`}>
                {d.getDate()}
              </div>
              <div className="mt-1 space-y-0.5">
                {items.slice(0, 3).map((it) => (
                  <ItemChip key={it.id} item={it} currency={currency} />
                ))}
                {items.length > 3 && (
                  <div className="text-[10px] text-muted px-1.5" data-testid={`calendar-more-${iso}`}>
                    +{items.length - 3} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AgendaView({ items, currency }) {
  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted" data-testid="calendar-agenda-empty">
        Nothing scheduled. Add events, payments, or tasks with due dates and they'll show up here.
      </div>
    );
  }
  // Group by date (already sorted by API).
  const groups = [];
  let current = null;
  for (const it of items) {
    if (!current || current.date !== it.date) {
      current = { date: it.date, items: [] };
      groups.push(current);
    }
    current.items.push(it);
  }
  return (
    <div className="space-y-4" data-testid="calendar-agenda-view">
      {groups.map((g) => (
        <div key={g.date}>
          <h4 className="font-mono text-xs uppercase tracking-wider text-muted mb-2">{formatAgendaDate(g.date)}</h4>
          <div className="space-y-1.5">
            {g.items.map((it) => <AgendaRow key={it.id} item={it} currency={currency} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function AgendaRow({ item, currency }) {
  const navigate = useNavigate();
  const meta = TYPE_META[item.type];
  const dim = (item.type === 'task' && item.completed) || (item.type === 'payment' && item.paid);
  const overdue = item.overdue;
  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 rounded-sm border ${overdue ? 'border-red-300 bg-red-50/40' : 'border-border bg-white'} ${dim ? 'opacity-60' : ''}`}
      data-testid={`calendar-agenda-row-${item.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Badge className={`${meta.bg} ${meta.text} ${meta.border} border text-xs uppercase tracking-wider flex-shrink-0`}>
          {meta.label.replace(/s$/, '')}
        </Badge>
        {overdue && (
          <Badge
            className="bg-red-50 text-red-700 border border-red-300 text-xs uppercase tracking-wider flex-shrink-0 flex items-center gap-1"
            data-testid={`calendar-agenda-overdue-${item.id}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-600" aria-hidden="true" />
            Overdue
          </Badge>
        )}
        <span className={`font-sans text-sm text-charcoal truncate ${dim ? 'line-through' : ''}`}>{item.title}</span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {item.type === 'payment' && (
          <span className="font-mono text-xs text-charcoal">
            {(item.currency || currency || '')} {Number(item.amount || 0).toLocaleString()}
          </span>
        )}
        <Button
          type="button"
          onClick={() => navigate(meta.dest)}
          className="text-xs text-muted hover:text-charcoal underline-offset-2 hover:underline"
          variant="ghost"
        >
          View
        </Button>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const { currentWedding } = useWedding();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIdx, setMonthIdx] = useState(today.getMonth());
  const [view, setView] = useState('month'); // 'month' | 'agenda'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ event: true, payment: true, task: true });

  const weddingId = currentWedding?.id;
  const currency = currentWedding?.preferred_currency;

  useEffect(() => {
    if (!weddingId) return;
    setLoading(true);
    axios.get(`${API}/calendar/${weddingId}`, { withCredentials: true })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [weddingId]);

  const filteredItems = useMemo(
    () => items.filter((i) => filters[i.type]),
    [items, filters],
  );

  const overdueCount = useMemo(
    () => filteredItems.filter((i) => i.overdue).length,
    [filteredItems],
  );

  const itemsByDate = useMemo(() => {
    const m = new Map();
    for (const it of filteredItems) {
      if (!m.has(it.date)) m.set(it.date, []);
      m.get(it.date).push(it);
    }
    return m;
  }, [filteredItems]);

  const goPrev = () => {
    if (monthIdx === 0) { setYear(year - 1); setMonthIdx(11); } else { setMonthIdx(monthIdx - 1); }
  };
  const goNext = () => {
    if (monthIdx === 11) { setYear(year + 1); setMonthIdx(0); } else { setMonthIdx(monthIdx + 1); }
  };
  const goToday = () => { setYear(today.getFullYear()); setMonthIdx(today.getMonth()); };

  return (
    <Layout>
      <div className="space-y-6" data-testid="calendar-page">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-4xl text-charcoal" data-testid="calendar-title">Calendar</h1>
            <p className="text-sm text-muted mt-1">Events, payments, and task due dates — all in one view.</p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => setView('month')}
              className={`text-xs ${view === 'month' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-1.5`}
              data-testid="calendar-view-month"
            >
              <CalendarIcon className="w-4 h-4" /> Month
            </Button>
            <Button
              type="button"
              onClick={() => setView('agenda')}
              className={`text-xs ${view === 'agenda' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-1.5`}
              data-testid="calendar-view-agenda"
            >
              <List className="w-4 h-4" /> Agenda
            </Button>
          </div>
        </div>

        <Card className="card-surface p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            {view === 'month' ? (
              <div className="flex items-center gap-2">
                <Button type="button" onClick={goPrev} className="btn-secondary text-xs h-8 w-8 p-0 flex items-center justify-center" data-testid="calendar-prev-month">
                  <NavArrowLeft className="w-4 h-4" />
                </Button>
                <h2 className="font-serif text-xl text-charcoal min-w-[180px] text-center" data-testid="calendar-current-month">
                  {MONTHS[monthIdx]} {year}
                </h2>
                <Button type="button" onClick={goNext} className="btn-secondary text-xs h-8 w-8 p-0 flex items-center justify-center" data-testid="calendar-next-month">
                  <NavArrowRight className="w-4 h-4" />
                </Button>
                <Button type="button" onClick={goToday} className="btn-secondary text-xs ml-2" data-testid="calendar-today-btn">Today</Button>
              </div>
            ) : (
              <h2 className="font-serif text-xl text-charcoal">Upcoming agenda</h2>
            )}
            <div className="flex items-center gap-3 flex-wrap" data-testid="calendar-filters">
              {Object.entries(TYPE_META).map(([type, meta]) => (
                <label key={type} className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-testid={`calendar-filter-${type}`}>
                  <Checkbox
                    checked={filters[type]}
                    onCheckedChange={(v) => setFilters((f) => ({ ...f, [type]: !!v }))}
                  />
                  <span className={`inline-block w-2 h-2 rounded-full ${meta.bg} border ${meta.border}`} />
                  <span className="font-mono uppercase tracking-wider text-charcoal">{meta.label}</span>
                </label>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12 text-muted">Loading calendar...</div>
          ) : (
            <>
              {overdueCount > 0 && (
                <button
                  type="button"
                  onClick={() => setView('agenda')}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-sm border border-red-300 bg-red-50 hover:bg-red-100 transition-colors"
                  data-testid="calendar-overdue-banner"
                >
                  <span className="flex items-center gap-2 text-sm text-red-800">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-600" aria-hidden="true" />
                    <span className="font-mono uppercase tracking-wider text-xs">Overdue</span>
                    <span data-testid="calendar-overdue-count">
                      {overdueCount} item{overdueCount === 1 ? '' : 's'} past their due date
                    </span>
                  </span>
                  <span className="text-xs font-mono text-red-700 uppercase tracking-wider">
                    {view === 'agenda' ? 'See below' : 'View in agenda →'}
                  </span>
                </button>
              )}
              {view === 'month' ? (
                <MonthView year={year} monthIdx={monthIdx} itemsByDate={itemsByDate} currency={currency} />
              ) : (
                <AgendaView items={filteredItems} currency={currency} />
              )}
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
}
